import { DataFieldOption } from '@models/settings/data-field-option.model';
import { DataField } from '@models/settings/data-field.model';

/*
 * This class is used to manage DataFieldOption state inside DataField create/edit forms
 */
export class DataFieldOptionStateManager {
    /*
     * Store any option persisted or dirty options that need to be created/updated
     */
    dataFieldOptions: DataFieldOption[] = [];
    /*
     * Store any options that need to be deleted
     */
    dataFieldOptionsToDelete: DataFieldOption[] = [];

    constructor(
        public dataField: DataField,
        private companyId: number
    ) {
        this.dataFieldOptions = [...this.dataField.dataFieldOptions];
    }

    /*
     * Update DataFieldOptions when the order changes in the drag-and-drop component
     */
    trackOptionChanges(newOptions: DataFieldOption[]): void {
        this.dataFieldOptions = [...newOptions];
    }

    removeDataFieldOption(DataFieldOptionIndex: number): void {
        const newDataFieldOptions = [...this.dataFieldOptions];
        const removedDataFieldOption = newDataFieldOptions.splice(DataFieldOptionIndex, 1)[0];

        this.dataFieldOptions = [...newDataFieldOptions];

        if (removedDataFieldOption.isPersisted) {
            this.dataFieldOptionsToDelete = [...this.dataFieldOptionsToDelete, removedDataFieldOption];
        }
    }

    shouldConfirmBeforeRemoving(DataFieldOptionIndex: number): boolean {
        const option = this.dataFieldOptions[DataFieldOptionIndex];

        if (option === undefined) {
            return false;
        }

        return this.dataFieldOptions[DataFieldOptionIndex].shouldConfirmBeforeRemove;
    }

    async save(): Promise<void> {
        const savableOptions = this.getSavableOptions();
        const onlyDirtyAndUnsavedOptions = this.getDirtyAndUnsavedOptions(savableOptions);
        await Promise.all(
            onlyDirtyAndUnsavedOptions.map((option) => {
                return option.param('company', this.companyId).param('dataField', this.dataField.id).save();
            })
        ).catch((error) => {
            throw new Error(error.message);
        });
    }

    async delete(): Promise<void> {
        await Promise.all(
            this.dataFieldOptionsToDelete.map((option) => {
                return option.param('company', this.companyId).param('dataField', this.dataField.id).delete();
            })
        ).catch((error) => {
            throw new Error(error.message);
        });

        this.dataFieldOptionsToDelete = [];
    }

    /*
     * Returns an array of valid options eg. no duplicates or empty values, if you
     * want to get the array of options which should be saved use getDirtyAndUnsavedOptions()
     */
    getSavableOptions(): DataFieldOption[] {
        const nonEmptyOptions = this.filterEmptyDataFieldOptions(this.dataFieldOptions);
        const uniqueOptions = this.filterDuplicateOptions(nonEmptyOptions);
        return this.setOrderBy(uniqueOptions);
    }

    /*
     * Only return options that values have changed or have not been persisted
     */
    private getDirtyAndUnsavedOptions(dataFieldOptions: DataFieldOption[]): DataFieldOption[] {
        return dataFieldOptions.filter((option) => {
            return !option.isPersisted || option.isDirty();
        });
    }

    /*
     * We want to remove any duplicate options before saving
     * Note: persisted options will not get filtered out only those are not persisted
     */
    private filterDuplicateOptions(dataFieldOptions: DataFieldOption[]): DataFieldOption[] {
        const values: string[] = [];
        const filteredOptions: DataFieldOption[] = [];

        dataFieldOptions.forEach((option) => {
            if (option.isPersisted) {
                values.push(option.value.toLowerCase());
                filteredOptions.push(option);
                return;
            }

            if (values.includes(option.value.toLowerCase())) {
                return;
            }

            values.push(option.value.toLowerCase());
            filteredOptions.push(option);
        });

        return filteredOptions;
    }

    /*
     * Remove any null/empty values before we save any options
     */
    private filterEmptyDataFieldOptions(dataFieldOptions: DataFieldOption[]): DataFieldOption[] {
        dataFieldOptions = dataFieldOptions.filter(
            (dataFieldOption: DataFieldOption) =>
                dataFieldOption.value !== undefined && dataFieldOption.value !== null && dataFieldOption.value !== ''
        );

        return dataFieldOptions;
    }

    /*
     * Set the orderBy so it indexed starting at 1
     */
    private setOrderBy(dataFieldOptions: DataFieldOption[]): DataFieldOption[] {
        dataFieldOptions.forEach((option, index) => {
            option.orderBy = index + 1;
        });

        return dataFieldOptions;
    }
}
